// src/component/Assistant/ChatWidget.jsx
// Bulle flottante (logo robot) → ouvre un panneau de chat IA.
// L'assistant appelle le backend /api/assistant/chat qui orchestre Claude + tes endpoints.
import { useState, useRef, useEffect } from "react";
import http from "../../api/http";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../context/brand-context";
import "./chatWidget.css";

const WELCOME = {
  role: "assistant",
  content:
    "Bonjour 👋 Je suis l'assistant Amen. Donnez-moi une référence, un VIN, ou décrivez la pièce recherchée.",
};

export default function ChatWidget() {
  const { user } = useAuth();
  const { brand } = useBrand();
  const dbName = brand === "amen" ? "Amen" : "Amen_C";

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]); // {role, content}
  const [items, setItems] = useState([]);              // articles réels (cartes)
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, items, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setErr("");
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");
    setLoading(true);
    try {
      // On n'envoie au back que les tours user/assistant (pas le message d'accueil purement UI).
      const payload = nextMessages
        .filter((_, i) => i !== 0)
        .map((m) => ({ role: m.role, content: m.content }));
      const { data } = await http.post(
        "/api/assistant/chat",
        { messages: payload, codtiers: user?.codtiers || null, db: dbName },
        // Flux long (login RepXpert + scraping + SQL + Claude) → timeout large,
        // au-delà des 15 s par défaut de l'instance http.
        { headers: { "x-db": dbName }, timeout: 120000 }
      );
      setMessages((prev) => [...prev, { role: "assistant", content: data?.reply || "…" }]);
      if (Array.isArray(data?.items) && data.items.length) setItems(data.items.slice(0, 12));
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Erreur réseau");
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Désolé, une erreur est survenue. Réessayez." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Bulle flottante */}
      <button
        className={`cw-fab ${open ? "cw-fab--open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Fermer l'assistant" : "Ouvrir l'assistant"}
      >
        {open ? (
          <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
            <path fill="currentColor" d="M18.3 5.71 12 12l6.3 6.29-1.42 1.42L10.59 13.4l-6.3 6.3-1.41-1.42L9.17 12 2.88 5.71 4.3 4.29l6.29 6.3 6.3-6.3z" />
          </svg>
        ) : (
          // Logo robot
          <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
            <path fill="currentColor" d="M12 2a1 1 0 0 1 1 1v1h3a3 3 0 0 1 3 3v1h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h1V7a3 3 0 0 1 3-3h3V3a1 1 0 0 1 1-1Zm-3 9a1.25 1.25 0 1 0 0 2.5A1.25 1.25 0 0 0 9 11Zm6 0a1.25 1.25 0 1 0 0 2.5A1.25 1.25 0 0 0 15 11Zm-5 5a1 1 0 0 0 0 2h4a1 1 0 0 0 0-2h-4Z" />
          </svg>
        )}
      </button>

      {/* Panneau de chat */}
      {open && (
        <div className="cw-panel" role="dialog" aria-label="Assistant Amen">
          <div className="cw-header">
            <span className="cw-dot" />
            <div className="cw-title">Assistant Amen</div>
            <button className="cw-close" onClick={() => setOpen(false)} aria-label="Fermer">×</button>
          </div>

          <div className="cw-body" ref={scrollRef}>
            {messages.map((m, i) => (
              <div key={i} className={`cw-msg cw-msg--${m.role}`}>{m.content}</div>
            ))}

            {items.length > 0 && (
              <div className="cw-items">
                {items.map((it, i) => (
                  <div key={`${it.origine}-${it.CodArt}-${i}`} className="cw-item">
                    <div className="cw-item-top">
                      <span className="cw-item-ref">{it.CodArt}</span>
                      <span className={`cw-badge ${(it.inStock ?? it.Qt > 0) ? "in" : "out"}`}>
                        {(it.inStock ?? it.Qt > 0) ? `En stock (${it.Qt})` : "Rupture"}
                      </span>
                    </div>
                    <div className="cw-item-lib">{it.LibArt}</div>
                    <div className="cw-item-bottom">
                      <span className="cw-item-brand">{it.Marque}</span>
                      {typeof it.PrixTTC === "number" && (
                        <span className="cw-item-price">{it.PrixTTC.toFixed(3)} TND</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {loading && (
              <div className="cw-msg cw-msg--assistant cw-typing">
                <span></span><span></span><span></span>
              </div>
            )}
          </div>

          {err && <div className="cw-err">{err}</div>}

          <div className="cw-input">
            <textarea
              rows={1}
              value={input}
              placeholder="Référence, VIN, ou description…"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={loading}
            />
            <button onClick={send} disabled={loading || !input.trim()} aria-label="Envoyer">
              <svg viewBox="0 0 24 24" width="20" height="20"><path fill="currentColor" d="M2 21l21-9L2 3v7l15 2-15 2z" /></svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
